import React from "react";
import { motion } from "framer-motion";

export default function TheBook() {
  return (
    <section
      id="the-book"
      data-testid="the-book-section"
      className="relative text-cream py-16 lg:py-24 overflow-hidden"
      style={{
        background:
          "radial-gradient(ellipse 90% 70% at 50% 55%, #2a1a0d 0%, #1a0f07 40%, #0d0703 75%, #050302 100%)",
      }}
    >
      <div className="max-w-[1400px] mx-auto container-x relative">
        {/* eyebrow */}
        <div className="flex items-center gap-4 mb-14 relative z-10">
          <span className="font-mono text-[10px] tracking-[0.28em] text-gold-bright">
            THE BOOK
          </span>
          <span className="flex-1 h-px bg-cream/15" />
        </div>

        {/* opening headline */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="font-display font-semibold text-[clamp(2rem,5.2vw,4.2rem)] leading-[1.05] tracking-[-0.015em] max-w-[18ch] mb-24 lg:mb-32 relative z-10"
        >
          One chapter, one company, one real story &mdash; told the way it
          actually happened.
        </motion.h2>

        {/* book + body */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* book column */}
          <div className="lg:col-span-5 flex justify-center lg:justify-start relative">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="bookv1-wrap"
            >
              {/* warm halo */}
              <div className="bookv1-wrap__halo" />

              {/* flame trails (SVG, blurred + glowing) */}
              <FlameTrails />

              {/* hover backlight — bright burst from behind the book */}
              <div className="bookv1-wrap__backlight" aria-hidden="true" />

              {/* book */}
              <div className="bookv1">
                <div className="bookv1__pages" />

                <div className="bookv1__cover">
                  {/* spine — vertical title on left edge */}
                  <div className="bookv1__spinetext" aria-hidden="true">
                    <span>
                      We Gotta <em>Gift</em>&nbsp;&nbsp;&middot;&nbsp;&nbsp;Vol. I
                    </span>
                  </div>

                  {/* top logo + label */}
                  <div className="relative z-[2] flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="rgba(220,140,60,0.95)"
                        strokeWidth="1.1"
                      >
                        <path d="M3 13.5 V5 a1 1 0 0 1 1 -1 h7.5 a2 2 0 0 1 2 2 v8" />
                        <path d="M3 13.5 a1.5 1.5 0 0 0 1.5 1.5 H13.5" />
                        <path d="M6 7.5 H10.5" />
                      </svg>
                      <span className="font-mono text-[9px] tracking-[0.28em] text-gold-bright/90">
                        WGG
                      </span>
                    </div>
                    <span className="font-mono text-[9px] tracking-[0.25em] text-cream/40">
                      VOL. I
                    </span>
                  </div>

                  {/* title — top half */}
                  <div className="relative z-[2] flex-1 flex flex-col items-start justify-center pt-6">
                    <h3 className="bookv1__title font-display font-bold text-[2.6rem] sm:text-[3rem] lg:text-[3.4rem] leading-[0.94] text-cream tracking-[-0.025em]">
                      We Gotta
                    </h3>
                    <h3
                      className="bookv1__title font-display italic font-semibold text-[2.6rem] sm:text-[3rem] lg:text-[3.4rem] leading-[0.94] -mt-1 tracking-[-0.02em]"
                      style={{
                        color: "#f0a040",
                        fontVariationSettings:
                          '"opsz" 144, "SOFT" 100, "WONK" 1',
                      }}
                    >
                      Gift
                    </h3>

                    <p className="mt-7 font-body text-[12px] sm:text-[13px] leading-[1.55] text-cream/70 max-w-[200px]">
                      The Stories Behind
                      <br />
                      the PPAI 100
                    </p>

                    <div className="bookv1__divider mt-7 w-[68%]" />
                  </div>

                  {/* bottom byline */}
                  <div className="relative z-[2] mt-auto">
                    <p className="font-mono text-[9px] tracking-[0.22em] text-cream/40 uppercase leading-[1.6]">
                      Edited by
                    </p>
                    <p className="font-display italic text-[15px] text-gold-bright/85 leading-tight mt-1">
                      Gaurav Bhagat
                    </p>
                  </div>

                  {/* shine sweep */}
                  <span className="bookv1__shine" />
                </div>
              </div>

              {/* base ember pool */}
              <div className="bookv1-wrap__floor" />
            </motion.div>
          </div>

          {/* copy column */}
          <div className="lg:col-span-7 lg:pl-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, delay: 0.15 }}
              className="inline-block mb-6 border border-cream/20 px-3 py-1.5"
            >
              <span className="font-mono text-[10px] tracking-[0.22em] text-gold-bright">
                COVER CONCEPT — VOL. I
              </span>
            </motion.div>

            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, delay: 0.2 }}
              className="font-display font-semibold text-[clamp(1.5rem,3vw,2.4rem)] leading-[1.15] tracking-[-0.01em] mb-10"
            >
              Behind every industry titan lies a formidable genesis story.
              Now, for the very first time, these unwritten histories are
              being etched into print.
            </motion.h3>

            <div className="space-y-6 text-[15px] lg:text-[16px] leading-[1.8] text-cream/75 max-w-xl">
              <p>
                Behind every top company in the industry is a powerful
                founding story&mdash;yet almost none have truly been told.
                This single, hardcover volume dedicates a chapter to each of
                the 30 featured businesses, moving past polished press
                releases to uncover the raw journeys. Inside are the real
                moments: the sparks of creation, the unspoken near-collapses,
                the high-stakes bets that paid off, and the core lessons that
                still drive these leaders today. Distributed widely across
                the global industry network and featured as a companion piece
                on{" "}
                <a
                  href="https://www.gauravbhagat.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cream underline decoration-gold/40 underline-offset-4 hover:decoration-gold-bright transition-colors"
                  data-testid="link-gauravbhagat"
                >
                  GauravBhagat.com
                </a>
                , this book is the definitive record of corporate grit and
                success.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Decorative ambient atmosphere behind the book.
 * Cinematic spotlight: warm haze, god-rays, drifting bokeh particles.
 */
const BOKEH = [
  // left side rising particles
  { cx: 16,  cy: 240, r: 1.6, delay: 0.0, dur: 7.5, sway: 14,  end: -200 },
  { cx: 28,  cy: 210, r: 1.2, delay: 1.6, dur: 6.8, sway: -8,  end: -180 },
  { cx: 10,  cy: 180, r: 2.0, delay: 3.2, dur: 8.2, sway: 18,  end: -220 },
  { cx: 22,  cy: 110, r: 1.4, delay: 2.4, dur: 7.8, sway: 12,  end: -190 },
  // right side rising particles
  { cx: 184, cy: 245, r: 1.7, delay: 0.8, dur: 7.6, sway: -16, end: -210 },
  { cx: 172, cy: 215, r: 1.2, delay: 2.2, dur: 7.0, sway: 9,   end: -180 },
  { cx: 192, cy: 185, r: 2.1, delay: 3.8, dur: 8.5, sway: -20, end: -230 },
  { cx: 188, cy: 110, r: 1.5, delay: 1.4, dur: 8.0, sway: -13, end: -200 },
  // close-to-cover micro dust
  { cx: 60,  cy: 260, r: 0.9, delay: 1.2, dur: 6.0, sway: 5,   end: -120 },
  { cx: 140, cy: 268, r: 0.9, delay: 3.6, dur: 6.2, sway: -5,  end: -130 },
];

function FlameTrails() {
  return (
    <svg
      className="bookv1-wrap__trails"
      viewBox="0 0 200 280"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
    >
      <defs>
        <radialGradient id="warmHaze" cx="50%" cy="60%" r="70%">
          <stop offset="0%" stopColor="#ff9445" stopOpacity="0.42" />
          <stop offset="45%" stopColor="#a44a14" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#000" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="goldSpark" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#ffe6b0" stopOpacity="1" />
          <stop offset="45%" stopColor="#ffb060" stopOpacity="0.85" />
          <stop offset="100%" stopColor="#ffb060" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="rayGrad" x1="0%" y1="100%" x2="0%" y2="0%">
          <stop offset="0%" stopColor="#ffc080" stopOpacity="0" />
          <stop offset="50%" stopColor="#ffc880" stopOpacity="0.22" />
          <stop offset="100%" stopColor="#ffc880" stopOpacity="0" />
        </linearGradient>
        <filter id="bigBlur" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="14" />
        </filter>
        <filter id="medBlur" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="7" />
        </filter>

        {/* slow wandering haze noise (static turbulence — animated baseFreq is expensive) */}
        <filter id="hazeNoise" x="-20%" y="-20%" width="140%" height="140%">
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.012 0.02"
            numOctaves="2"
            seed="5"
            result="turb"
          />
          <feDisplacementMap in="SourceGraphic" in2="turb" scale="6" />
        </filter>
      </defs>

      {/* deep warm spotlight halo */}
      <ellipse
        cx="100"
        cy="200"
        rx="130"
        ry="180"
        fill="url(#warmHaze)"
        filter="url(#bigBlur)"
      />

      {/* god-rays radiating up from behind the book */}
      <g className="rays" filter="url(#bigBlur)">
        <polygon points="100,160 60,-20 140,-20" fill="url(#rayGrad)" />
        <polygon points="100,160 10,40 80,-20" fill="url(#rayGrad)" opacity="0.65" />
        <polygon points="100,160 120,-20 190,40" fill="url(#rayGrad)" opacity="0.65" />
        <polygon points="100,160 -10,120 40,-20" fill="url(#rayGrad)" opacity="0.4" />
        <polygon points="100,160 160,-20 210,120" fill="url(#rayGrad)" opacity="0.4" />
      </g>

      {/* drifting warm glow blobs (organic wander via turbulence) */}
      <g filter="url(#hazeNoise)" opacity="0.85">
        <ellipse cx="48" cy="220" rx="40" ry="55" fill="#c85820" opacity="0.55" filter="url(#bigBlur)" />
        <ellipse cx="160" cy="200" rx="50" ry="65" fill="#dd6c28" opacity="0.55" filter="url(#bigBlur)" />
        <ellipse cx="100" cy="270" rx="85" ry="32" fill="#ff8a3c" opacity="0.45" filter="url(#bigBlur)" />
        <ellipse cx="174" cy="100" rx="22" ry="40" fill="#e88838" opacity="0.32" filter="url(#bigBlur)" />
        <ellipse cx="30" cy="120" rx="20" ry="38" fill="#e88838" opacity="0.32" filter="url(#bigBlur)" />
      </g>

      {/* tiny anchor embers (gentle constant twinkles) */}
      <circle cx="178" cy="225" r="2.2" fill="url(#goldSpark)" className="ember-twinkle" />
      <circle cx="188" cy="180" r="1.5" fill="url(#goldSpark)" className="ember-twinkle" style={{ animationDelay: "1.4s" }} />
      <circle cx="22" cy="215" r="2.0" fill="url(#goldSpark)" className="ember-twinkle" style={{ animationDelay: "2.6s" }} />
      <circle cx="14" cy="160" r="1.3" fill="url(#goldSpark)" className="ember-twinkle" style={{ animationDelay: "0.7s" }} />
      <circle cx="192" cy="120" r="1.3" fill="url(#goldSpark)" className="ember-twinkle" style={{ animationDelay: "3.2s" }} />

      {/* drifting golden bokeh dust — slow upward float */}
      <g className="bokeh">
        {BOKEH.map((b, i) => (
          <circle
            key={i}
            cx={b.cx}
            cy={b.cy}
            r={b.r}
            fill="url(#goldSpark)"
            className="bokeh-dot"
            style={{
              animationDelay: `${b.delay}s`,
              animationDuration: `${b.dur}s`,
              "--sway-end": `${b.sway}px`,
              "--rise-end": `${b.end}px`,
            }}
          />
        ))}
      </g>
    </svg>
  );
}
